import { FieldType, getHistory, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { InductionAdminEditModel } from "./InductionAdminEditModel";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { AppUrls } from "AppUrls";
import { InductionQualificationDTO } from "../InductionAdminItemModel";
import { QualificationEditViewModel } from "./ViewModels/QualificationEditViewModel";
import { PPEEditViewModel } from "./ViewModels/PPEEditViewModel";
import { PersonalProtectiveEquipment } from "Globals/Models/Enums/PersonalProtectiveEquipment";
import { PersonalProtectiveEquipmentType } from "Globals/Models/Enums/PersonalProtectiveEquipmentType";
import { AdminInductionRequest, AdminQualification } from "./Models/AdminInductionRequest";
import { SupplierModel } from "Views/PurchaseOrder/Form/Supporting/SupplierModel";
//import { InductionQualificationDTO } from "Models/InductionQualificationDTO";

export class InductionAdminEditViewModel extends ViewModelBase<InductionAdminEditModel> {
    public server: ServerViewModel = new ServerViewModel();
    public skillsQualifications: QualificationEditViewModel[] = [];
    public plantQualifications: QualificationEditViewModel[] = [];
    @observable public suppliers: SupplierModel[] = [];

    @observable
    public ppeItems: PPEEditViewModel[] = [];

    constructor(suppliers: SupplierModel[]) {
        super(new InductionAdminEditModel());
        this.setDecorators(this.model);
        this.suppliers = suppliers;
    }

    @action
    public initializeFromModel = () => {
        // Initialize skills qualifications
        this.skillsQualifications = this.model.skillsTrainingOrQualifications.map((qual) => new QualificationEditViewModel(qual));

        // Initialize plant qualifications
        this.plantQualifications = this.model.plantAndEquipmentTrainingOrQualifications.map((qual) => new QualificationEditViewModel(qual));

        // Initialize PPE arrays with proper observable arrays
        this.model.personalProtectiveEquipment = observable([...(this.model.personalProtectiveEquipment || [])]);
        this.model.personalProtectiveEquipmentTypes = observable([...(this.model.personalProtectiveEquipmentTypes || [])]);
        this.model.selectedPersonalProtectiveEquipmentTypes = observable([...(this.model.selectedPersonalProtectiveEquipmentTypes || [])]);
    };

    @action
    public updateModel = () => {
        // Update main model from view models
        this.model.skillsTrainingOrQualifications = this.skillsQualifications.map((vm) => vm.model.toDto());
        this.model.plantAndEquipmentTrainingOrQualifications = this.plantQualifications.map((vm) => vm.model.toDto());
        // No need for special PPE handling as it's already in the model
    };

    @computed
    public get supplierType() {
        const result = this.suppliers.find((p: any) => p.id === this.model.supplierId);
        return result ? result! : null;
    }

    public setSupplierType = (value: SupplierModel) => {
        this.setValue("supplierId", value.id);
        this.setValue("priorityEmployerName", value.displayName);
    };

    @action
    public setHealthProblems = (value: boolean) => {
        this.model.healthProblems = value;
    };

    @action
    public setHealthDetails = (value: string) => {
        this.model.healthDetails = value;
    };

    @action
    public setConfirmOver18 = (value: boolean) => {
        this.model.confirmOver18 = value;
    };

    @action
    public setConfirmAdjustments = (value: boolean) => {
        this.model.confirmAdjustments = value;
    };

    @action
    public setAdjustmentDetails = (value: string) => {
        this.model.adjustmentDetails = value;
    };

    @action
    public updateQualificationPassed = (type: string, index: number, value: boolean) => {
        if (type === "skillsTrainingOrQualifications") {
            this.model.skillsTrainingOrQualifications[index].passed = value;
        } else {
            this.model.plantAndEquipmentTrainingOrQualifications[index].passed = value;
        }
    };

    @action
    public addQualification = (type: string) => {
        const newQual: InductionQualificationDTO = {
            id: "",
            inductionUserId: this.model.id,
            inductionSectionTypeId: type,
            idRollNumber: "",
            name: "",
            passed: null,
            photoEvidenceData: "",
            photoEvidenceFileName: "",
            photoEvidenceUrl: "",
            photoEvidenceUrlInline: "",
            typeLevel: "",
            expiryDate: "",
        };

        if (type === "skillsTrainingOrQualifications") {
            this.model.skillsTrainingOrQualifications.push(newQual);
        } else {
            this.model.plantAndEquipmentTrainingOrQualifications.push(newQual);
        }
    };

    @action
    public togglePersonalProtectiveEquipmentType = (ppeTypeId: string) => {
        const currentIndex = this.model.selectedPersonalProtectiveEquipmentTypes.findIndex((ppeType) => ppeType.id === ppeTypeId);

        if (currentIndex > -1) {
            // Remove if found
            this.model.selectedPersonalProtectiveEquipmentTypes.splice(currentIndex, 1);
        } else {
            // Add if not found
            const ppeTypeToAdd = this.model.personalProtectiveEquipmentTypes.find((ppeType) => ppeType.id === ppeTypeId);
            if (ppeTypeToAdd) {
                this.model.selectedPersonalProtectiveEquipmentTypes.push(ppeTypeToAdd);
            }
        }
    };

    public isPersonalProtectiveEquipmentTypeSelected = (ppeTypeId: string) => {
        return this.model.selectedPersonalProtectiveEquipmentTypes.findIndex((ppeType) => ppeType.id === ppeTypeId) > -1;
    };

    public getPersonalProtectiveEquipmentTypesByGroupId = (personalProtectiveEquipmentId: string) => {
        return this.model.personalProtectiveEquipmentTypes.filter((ppet) => ppet.personalProtectiveEquipmentId === personalProtectiveEquipmentId);
    };

    @computed
    public get personalProtectiveEquipment() {
        return this.model.personalProtectiveEquipment;
    }

    @action
    public setFirstName = (value: string) => {
        this.model.firstName = value;
    };

    @action
    public setLastName = (value: string) => {
        this.model.lastName = value;
    };

    @computed
    public get fullName() {
        return `${this.model.firstName} ${this.model.lastName}`.trim();
    }

    private mapToAdminRequest(): AdminInductionRequest {
        return {
            id: this.model.id,
            age: this.model.age,
            confirmAdjustments: this.model.confirmAdjustments,
            confirmOver18: this.model.confirmOver18,
            healthProblems: this.model.healthProblems,
            provideAdjustmentDetails: this.model.adjustmentDetails || "",
            provideHealthDetails: this.model.healthDetails || "",
            supplierId: this.model.supplierId,
            priorityEmployerName: this.model.priorityEmployerName,
            skillQualification: this.skillsQualifications.map((q) => ({
                id: q.model.id,
                inductionUserId: this.model.id,
                inductionSectionTypeId: q.model.inductionSectionTypeId,
                idRollNumber: q.model.idRollNumber,
                name: q.model.name,
                passed: q.model.passed,
                photoEvidenceData: q.model.photoEvidenceData,
                photoEvidenceFileName: q.model.photoEvidenceFileName,
                photoEvidenceUrl: q.model.photoEvidenceUrl,
                typeLevel: q.model.typeLevel,
                expiryDate: q.model.expiryDate,
            })),
            plantQualifications: this.plantQualifications.map((q) => ({
                id: q.model.id,
                inductionUserId: this.model.id,
                inductionSectionTypeId: q.model.inductionSectionTypeId,
                idRollNumber: q.model.idRollNumber,
                name: q.model.name,
                passed: q.model.passed,
                photoEvidenceData: q.model.photoEvidenceData,
                photoEvidenceFileName: q.model.photoEvidenceFileName,
                photoEvidenceUrl: q.model.photoEvidenceUrl,
                typeLevel: q.model.typeLevel,
                expiryDate: q.model.expiryDate,
            })),
            personalProtectiveEquipmentTypeIds: this.model.selectedPersonalProtectiveEquipmentTypes.map((ppe) => ppe.id),
        };
    }

    public saveInduction = async (): Promise<void> => {
        return this.server.command(
            () => this.Post(AppUrls.Server.Induction.UpdateInduction, this.mapToAdminRequest()),
            () => {
                runInAction(() => {
                    getHistory().goBack();
                });
            },
            () => this.isModelValid(),
            "Failed to update induction",
        );
    };

    public async isFieldValid(fieldName: keyof FieldType<InductionAdminEditModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
